import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeft, GraduationCap, Trophy, Users, BookOpen, Target,
  Sparkles, Loader2, Plus, Brain, MapPin, Lightbulb, CheckCircle,
  FileText, Calculator, Flask, Palette, Globe, ChevronRight, Wand2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [standards, setStandards] = useState({});
  const [fiveEs, setFiveEs] = useState({});
  const [challenges, setChallenges] = useState([]);
  
  // AI Generator state
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedIdea, setGeneratedIdea] = useState(null);
  const [generatorForm, setGeneratorForm] = useState({
    standards: [],
    grade_level: "6-8",
    theme: "food_security",
    location: "Caribbean",
    focus_area: "general"
  });

  const loadTeacherData = useCallback(async () => {
    try {
      const [profileRes, standardsRes, fiveEsRes, challengesRes] = await Promise.all([
        axios.get(`${API}/teachers/profile`, { withCredentials: true }),
        axios.get(`${API}/educational-standards`),
        axios.get(`${API}/teachers/five-es`),
        axios.get(`${API}/challenges`, { withCredentials: true })
      ]);
      
      setTeacherProfile(profileRes.data);
      setStandards(standardsRes.data.standards || {});
      setFiveEs(fiveEsRes.data.framework || {});
      setChallenges(challengesRes.data.challenges?.filter(c => c.teacher_id === user?.user_id) || []);
    } catch (error) {
      console.error("Failed to load teacher data:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (user) {
      loadTeacherData();
    } else {
      setLoading(false);
    }
  }, [user, loadTeacherData]);

  const generateChallengeIdea = async () => {
    if (generatorForm.standards.length === 0) {
      toast.error("Please select at least one standard");
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/teachers/generate-challenge`,
        generatorForm,
        { withCredentials: true }
      );
      setGeneratedIdea(response.data.challenge_idea);
      toast.success("Challenge idea generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate idea");
    } finally {
      setGenerating(false);
    }
  };

  const createChallengeFromIdea = async () => {
    if (!generatedIdea) return;
    
    try {
      const payload = {
        title: generatedIdea.title,
        description: generatedIdea.description,
        prompt: generatedIdea.prompt,
        theme: generatedIdea.theme,
        local_theme: generatedIdea.location,
        grade_level: generatedIdea.grade_level,
        educational_standards: generatedIdea.standards_used,
        learning_objectives: generatedIdea.learning_objectives,
        five_es_activities: generatedIdea.five_es,
        writing_prompt: generatedIdea.writing_prompt,
        min_plants: generatedIdea.suggested_goals?.min_plants || 5,
        max_plants: generatedIdea.suggested_goals?.max_plants || 20,
        yield_goal_lbs: generatedIdea.suggested_goals?.yield_lbs,
        calorie_goal: generatedIdea.suggested_goals?.calorie_goal,
        end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      };
      
      await axios.post(`${API}/challenges`, payload, { withCredentials: true });
      toast.success("Challenge created!");
      setGeneratedIdea(null);
      setGeneratorOpen(false);
      loadTeacherData();
    } catch (error) {
      toast.error("Failed to create challenge");
    }
  };

  const toggleStandard = (code) => {
    setGeneratorForm(prev => ({
      ...prev,
      standards: prev.standards.includes(code)
        ? prev.standards.filter(s => s !== code)
        : [...prev.standards, code]
    }));
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 text-blue-500" />
            <h2 className="text-xl font-bold mb-2">Teacher Dashboard</h2>
            <p className="text-muted-foreground mb-6">Sign in to access your teacher tools.</p>
            <Button onClick={login} size="lg">Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!teacherProfile?.is_teacher) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 text-blue-500" />
            <h2 className="text-xl font-bold mb-2">Become a Teacher</h2>
            <p className="text-muted-foreground mb-6">
              Register as a teacher to create GROWTH Challenges for your students.
            </p>
            <Button onClick={() => navigate('/challenges/teacher-register')} size="lg">
              Register Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/challenges")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> GROWTH
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-blue-500" />
                Teacher Dashboard
              </h1>
              <p className="text-sm text-muted-foreground">{teacherProfile.school_name}</p>
            </div>
          </div>
          <Button onClick={() => setGeneratorOpen(true)} data-testid="ai-generator-btn">
            <Wand2 className="w-4 h-4 mr-2" /> AI Challenge Generator
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{teacherProfile.challenge_count}</p>
                  <p className="text-sm text-muted-foreground">Challenges Created</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{teacherProfile.total_student_submissions}</p>
                  <p className="text-sm text-muted-foreground">Student Submissions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{Object.keys(standards).length}</p>
                  <p className="text-sm text-muted-foreground">Standard Sets Available</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Generator Modal */}
        <AnimatePresence>
          {generatorOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
              onClick={() => !generating && setGeneratorOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-t-2xl">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Brain className="w-5 h-5" />
                    AI Challenge Generator (5 E&apos;s Format)
                  </h2>
                  <p className="text-white/80 text-sm mt-1">
                    Generate standards-aligned GROWTH Challenges using the 5 E&apos;s instructional model
                  </p>
                </div>

                <div className="p-6 space-y-6">
                  {!generatedIdea ? (
                    <>
                      {/* Grade Level & Theme */}
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Grade Level</Label>
                          <select
                            value={generatorForm.grade_level}
                            onChange={(e) => setGeneratorForm(prev => ({ ...prev, grade_level: e.target.value }))}
                            className="w-full p-2 border rounded-lg"
                          >
                            <option value="K-2">K-2 (Early Elementary)</option>
                            <option value="3-5">3-5 (Upper Elementary)</option>
                            <option value="6-8">6-8 (Middle School)</option>
                            <option value="9-12">9-12 (High School)</option>
                          </select>
                        </div>
                        <div>
                          <Label>Theme</Label>
                          <select
                            value={generatorForm.theme}
                            onChange={(e) => setGeneratorForm(prev => ({ ...prev, theme: e.target.value }))}
                            className="w-full p-2 border rounded-lg"
                          >
                            <option value="food_security">Food Security</option>
                            <option value="pollinator">Pollinator Paradise</option>
                            <option value="native_plants">Native Plants</option>
                            <option value="water_wise">Water-Wise Garden</option>
                            <option value="three_sisters">Three Sisters</option>
                            <option value="kitchen_garden">Kitchen Garden</option>
                          </select>
                        </div>
                        <div>
                          <Label>Focus Area</Label>
                          <select
                            value={generatorForm.focus_area}
                            onChange={(e) => setGeneratorForm(prev => ({ ...prev, focus_area: e.target.value }))}
                            className="w-full p-2 border rounded-lg"
                          >
                            <option value="general">General</option>
                            <option value="writing">Writing Focus</option>
                            <option value="math">Math Focus</option>
                            <option value="science">Science Focus</option>
                            <option value="design">Design Focus</option>
                          </select>
                        </div>
                      </div>

                      {/* Location */}
                      <div>
                        <Label>Location Context (for writing prompts)</Label>
                        <Input
                          value={generatorForm.location}
                          onChange={(e) => setGeneratorForm(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="e.g., Caribbean, US Virgin Islands, St. Thomas"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          The AI will incorporate local climate, flora, and cultural gardening traditions
                        </p>
                      </div>

                      {/* Standards Selection */}
                      <div>
                        <Label className="mb-3 block">Select Educational Standards</Label>
                        <Tabs defaultValue="visa_ela" className="w-full">
                          <TabsList className="grid grid-cols-5 w-full">
                            <TabsTrigger value="visa_ela" className="text-xs">VISA ELA</TabsTrigger>
                            <TabsTrigger value="visa_math" className="text-xs">VISA Math</TabsTrigger>
                            <TabsTrigger value="ngss" className="text-xs">NGSS</TabsTrigger>
                            <TabsTrigger value="cte" className="text-xs">CTE Ag</TabsTrigger>
                            <TabsTrigger value="ccss_math" className="text-xs">CCSS Math</TabsTrigger>
                          </TabsList>
                          
                          {Object.entries(standards).map(([key, std]) => (
                            <TabsContent key={key} value={key} className="mt-4">
                              <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-1">
                                {std.standards?.map(s => (
                                  <label 
                                    key={s.code} 
                                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                      generatorForm.standards.includes(s.code) 
                                        ? 'bg-blue-50 border border-blue-200' 
                                        : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={generatorForm.standards.includes(s.code)}
                                      onChange={() => toggleStandard(s.code)}
                                      className="rounded"
                                    />
                                    <span className="font-mono text-xs text-blue-600 font-semibold">{s.code}</span>
                                    <span className="text-sm flex-1">{s.description}</span>
                                  </label>
                                ))}
                              </div>
                            </TabsContent>
                          ))}
                        </Tabs>
                        
                        {generatorForm.standards.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="text-sm font-medium">Selected:</span>
                            {generatorForm.standards.map(code => (
                              <Badge key={code} variant="secondary" className="text-xs">
                                {code}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* 5 E's Reference */}
                      <div className="bg-purple-50 rounded-xl p-4">
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <Lightbulb className="w-5 h-5 text-purple-600" />
                          5 E&apos;s Instructional Model
                        </h3>
                        <div className="grid grid-cols-5 gap-2 text-center text-xs">
                          {Object.entries(fiveEs).map(([key, phase]) => (
                            <div key={key} className="bg-white rounded-lg p-2">
                              <p className="font-semibold capitalize text-purple-700">{key}</p>
                              <p className="text-muted-foreground mt-1">{phase.description?.split(' ').slice(0, 4).join(' ')}...</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Button 
                        onClick={generateChallengeIdea} 
                        disabled={generating || generatorForm.standards.length === 0}
                        className="w-full"
                        size="lg"
                      >
                        {generating ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Sparkles className="w-5 h-5 mr-2" />
                        )}
                        Generate Challenge Idea
                      </Button>
                    </>
                  ) : (
                    /* Generated Idea Display */
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6">
                        <Badge className="mb-2">{generatedIdea.theme}</Badge>
                        <h3 className="text-2xl font-bold mb-2">{generatedIdea.title}</h3>
                        <p className="text-muted-foreground">{generatedIdea.description}</p>
                      </div>

                      <div>
                        <h4 className="font-semibold mb-2">Challenge Prompt</h4>
                        <p className="bg-gray-50 p-4 rounded-lg">{generatedIdea.prompt}</p>
                      </div>

                      {/* 5 E's Activities */}
                      <div>
                        <h4 className="font-semibold mb-3">5 E&apos;s Activities</h4>
                        <div className="space-y-3">
                          {generatedIdea.five_es && Object.entries(generatedIdea.five_es).map(([phase, content]) => (
                            <div key={phase} className="border rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="capitalize">{phase}</Badge>
                                {phase === 'engage' && <Lightbulb className="w-4 h-4 text-amber-500" />}
                                {phase === 'explore' && <Globe className="w-4 h-4 text-blue-500" />}
                                {phase === 'explain' && <FileText className="w-4 h-4 text-green-500" />}
                                {phase === 'elaborate' && <Brain className="w-4 h-4 text-purple-500" />}
                                {phase === 'evaluate' && <Target className="w-4 h-4 text-red-500" />}
                              </div>
                              <p className="text-sm mb-1">{content.activity}</p>
                              <p className="text-xs text-muted-foreground">
                                <span className="font-medium">Standard:</span> {content.standard_connection}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Writing Prompt */}
                      <div className="bg-amber-50 rounded-xl p-4">
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <FileText className="w-5 h-5 text-amber-600" />
                          Writing Prompt
                        </h4>
                        <p className="text-sm">{generatedIdea.writing_prompt}</p>
                      </div>

                      {/* Goals */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <p className="text-2xl font-bold text-green-700">{generatedIdea.suggested_goals?.yield_lbs}</p>
                          <p className="text-xs text-green-600">lbs yield goal</p>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded-lg">
                          <p className="text-2xl font-bold text-orange-700">{generatedIdea.suggested_goals?.calorie_goal?.toLocaleString()}</p>
                          <p className="text-xs text-orange-600">calorie goal</p>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <p className="text-2xl font-bold text-blue-700">{generatedIdea.suggested_goals?.min_plants}</p>
                          <p className="text-xs text-blue-600">min plants</p>
                        </div>
                        <div className="text-center p-3 bg-purple-50 rounded-lg">
                          <p className="text-2xl font-bold text-purple-700">{generatedIdea.suggested_goals?.max_plants}</p>
                          <p className="text-xs text-purple-600">max plants</p>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button variant="outline" onClick={() => setGeneratedIdea(null)} className="flex-1">
                          Generate New Idea
                        </Button>
                        <Button onClick={createChallengeFromIdea} className="flex-1">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Create This Challenge
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* My Challenges */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">My GROWTH Challenges</h2>
            <Button onClick={() => setGeneratorOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> New Challenge
            </Button>
          </div>

          {challenges.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {challenges.map(challenge => (
                <Card 
                  key={challenge.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/challenges?view=${challenge.id}`)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <Badge variant="outline">{challenge.theme}</Badge>
                      <Badge className={challenge.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}>
                        {challenge.status}
                      </Badge>
                    </div>
                    <h3 className="font-bold mb-2">{challenge.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                      {challenge.description || challenge.prompt}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Users className="w-4 h-4" />
                        {challenge.submission_count || 0} submissions
                      </span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Trophy className="w-12 h-12 mx-auto mb-4 text-amber-300" />
                <h3 className="text-lg font-semibold mb-2">No Challenges Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Use the AI Challenge Generator to create your first standards-aligned GROWTH Challenge!
                </p>
                <Button onClick={() => setGeneratorOpen(true)}>
                  <Wand2 className="w-4 h-4 mr-2" /> Generate Challenge
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default TeacherDashboard;
